import type {
  KeycloakUserData,
  User as BackendUser,
} from '@liks/backend-svc-api/esm/models/index';
import { ResponseError } from '@liks/backend-svc-api/esm/runtime';
import type { WithResourceId } from '../types/knowledge';
import type {
  AssignedDirectoryUser,
  DirectoryUserWithId,
} from './userServiceTypes';

export function hasUserId(user: KeycloakUserData): user is DirectoryUserWithId {
  return typeof user.id === 'string' && user.id.length > 0;
}

export function hasResourceId<T extends { id?: string | null }>(
  value: T,
): value is WithResourceId<T> {
  return typeof value.id === 'string' && value.id.length > 0;
}

export function hasNamedResourceId<
  T extends { id?: string | null; name?: string | null },
>(value: T): value is WithResourceId<T> & { name: string } {
  return (
    hasResourceId(value) &&
    typeof value.name === 'string' &&
    value.name.length > 0
  );
}

export function toAssignedDirectoryUser(
  user: BackendUser,
): AssignedDirectoryUser | null {
  // On label/group membership list rows, `id` is the assigned member (person).
  // `userId` is the User-row creator (audit) and must not be used as identity.
  const id = typeof user.id === 'string' && user.id.length > 0 ? user.id : null;
  if (!id) {
    return null;
  }

  return {
    id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    name: user.name,
    thumbnail: user.thumbnail,
  };
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function isUserNotFoundError(error: unknown): boolean {
  if (error instanceof ResponseError && error.response.status === 404) {
    return true;
  }
  if (error instanceof Error) {
    return /user not found/i.test(error.message);
  }
  return false;
}
